import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
  CircularProgress,
  Paper,
  InputAdornment,
  Divider,
  TablePagination,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import { StyledContainer } from '../../dynamicform/styled.js';
import { validateAuth, getApiBaseUrl } from '../../util/util.js';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { userTypeSelector } from '../../redux/selectors.js';
import { notificationSuccess, notificationError } from '../../redux/actions/app.js';

const ALL_OPTION = 'All';

export default function EarlyAccessTab() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const currentUserType = useSelector(userTypeSelector);
  const apiBase = process.env.REACT_APP_AWS_API_URL;

  // Allowed emails display
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [loadingAllowed, setLoadingAllowed] = useState(false);

  // Add dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  // multi-select: Set of contact emails
  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [adding, setAdding] = useState(false);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [filterName, setFilterName] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterCustomer, setFilterCustomer] = useState(ALL_OPTION);
  const [filterDatacentre, setFilterDatacentre] = useState(ALL_OPTION);

  const [appliedName, setAppliedName] = useState('');
  const [appliedEmail, setAppliedEmail] = useState('');
  const [appliedCustomer, setAppliedCustomer] = useState(ALL_OPTION);
  const [appliedDatacentre, setAppliedDatacentre] = useState(ALL_OPTION);

  const customerOptions = useMemo(() => {
    const set = new Set();
    contacts.forEach((c) => (c.customers || []).forEach((cu) => set.add(cu)));
    return [ALL_OPTION, ...Array.from(set).sort()];
  }, [contacts]);

  const datacentreOptions = useMemo(() => {
    const set = new Set();
    contacts.forEach((c) => (c.datacentres || []).forEach((dc) => set.add(dc)));
    return [ALL_OPTION, ...Array.from(set).sort()];
  }, [contacts]);

  const filteredContacts = useMemo(() => {
    return contacts.filter((c) => {
      const nameMatch = !appliedName || c.name?.toLowerCase().includes(appliedName.toLowerCase());
      const emailMatch =
        !appliedEmail || c.email?.toLowerCase().includes(appliedEmail.toLowerCase());
      const customerMatch =
        appliedCustomer === ALL_OPTION || (c.customers || []).includes(appliedCustomer);
      const datacentreMatch =
        appliedDatacentre === ALL_OPTION || (c.datacentres || []).includes(appliedDatacentre);
      return nameMatch && emailMatch && customerMatch && datacentreMatch;
    });
  }, [contacts, appliedName, appliedEmail, appliedCustomer, appliedDatacentre]);

  const allFilteredSelected =
    filteredContacts.length > 0 && filteredContacts.every((c) => selectedContacts.has(c.email));
  const someFilteredSelected =
    filteredContacts.some((c) => selectedContacts.has(c.email)) && !allFilteredSelected;

  const handleApplySearch = () => {
    setAppliedName(filterName);
    setAppliedEmail(filterEmail);
    setAppliedCustomer(filterCustomer);
    setAppliedDatacentre(filterDatacentre);
    setPage(0);
  };

  const pagedContacts = useMemo(
    () => filteredContacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredContacts, page, rowsPerPage]
  );

  const toggleContact = (email) => {
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      next.has(email) ? next.delete(email) : next.add(email);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelectedContacts((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredContacts.forEach((c) => next.delete(c.email));
      } else {
        filteredContacts.forEach((c) => next.add(c.email));
      }
      return next;
    });
  };

  const fetchAllowedEmails = async () => {
    setLoadingAllowed(true);
    await validateAuth(async (session) => {
      try {
        const response = await axios.get(`${apiBase}/early_access/get_emails`, {
          headers: { Authorization: session.getIdToken().getJwtToken() },
          timeout: 15000,
        });
        setAllowedEmails(response?.data?.data || []);
      } catch {
        setAllowedEmails([]);
        dispatch(notificationError('Failed to load allowed emails'));
      } finally {
        setLoadingAllowed(false);
      }
    });
  };

  const fetchContacts = async () => {
    setLoadingContacts(true);
    await validateAuth(async (session) => {
      try {
        const response = await axios.get(`${apiBase}/early_access/get_contacts`, {
          headers: { Authorization: session.getIdToken().getJwtToken() },
          timeout: 15000,
        });
        setContacts(response?.data?.data || []);
      } catch {
        setContacts([]);
        dispatch(notificationError('Failed to load contacts'));
      } finally {
        setLoadingContacts(false);
      }
    });
  };

  const handleAdd = async () => {
    if (selectedContacts.size === 0) return;
    setAdding(true);
    const payload = contacts
      .filter((c) => selectedContacts.has(c.email))
      .map((c) => ({ email: c.email, contactId: c.id }));
    await validateAuth(async (session) => {
      try {
        await axios.post(
          `${apiBase}/early_access/create_emails`,
          { contacts: payload },
          {
            headers: { Authorization: session.getIdToken().getJwtToken() },
            timeout: 15000,
          }
        );
        dispatch(notificationSuccess(`${payload.length} email(s) added to Beta Access`));
        await fetchAllowedEmails();
        handleCloseDialog();
      } catch {
        dispatch(notificationError('Failed to add emails to Beta Access'));
      } finally {
        setAdding(false);
      }
    });
  };

  const handleRemove = async (entry) => {
    await validateAuth(async (session) => {
      try {
        await axios.delete(`${apiBase}/early_access/destroy_emails/${entry.id}`, {
          headers: { Authorization: session.getIdToken().getJwtToken() },
          timeout: 15000,
        });
        dispatch(notificationSuccess(`${entry.email} removed from Beta Access`));
        await fetchAllowedEmails();
      } catch {
        dispatch(notificationError('Failed to remove email'));
      }
    });
  };

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
    setSelectedContacts(new Set());
    setFilterName('');
    setFilterEmail('');
    setFilterCustomer(ALL_OPTION);
    setFilterDatacentre(ALL_OPTION);
    setAppliedName('');
    setAppliedEmail('');
    setAppliedCustomer(ALL_OPTION);
    setAppliedDatacentre(ALL_OPTION);
    setPage(0);
    setRowsPerPage(25);
    fetchContacts();
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedContacts(new Set());
  };

  useEffect(() => {
    fetchAllowedEmails();
  }, []);

  return (
    <Box sx={{ p: theme.spacing(3), display: 'flex', flexDirection: 'column', gap: 3 }}>
      <StyledContainer>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddAlt1Icon color="tertiary" />
            <Typography variant="overline">Allowed Emails</Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleOpenDialog}
          >
            Add Email
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loadingAllowed ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : allowedEmails.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No allowed emails configured yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography variant="overline">Name</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="overline">Email</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="overline">Datacentres</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="overline">Customers</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="overline">Actions</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allowedEmails.map((entry) => (
                  <TableRow key={entry.id ?? entry.email} hover>
                    <TableCell>{entry.name ?? '—'}</TableCell>
                    <TableCell>{entry.email}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(entry.datacentres || []).map((dc) => (
                          <Chip key={dc} label={dc} size="small" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(entry.customers || []).map((cu) => (
                          <Chip key={cu} label={cu} size="small" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => handleRemove(entry)}>
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </StyledContainer>

      <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>Add Email to Beta Access</DialogTitle>

        <DialogContent dividers>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mb: 2,
            }}
          >
            <TextField
              label="Filter by Name"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplySearch()}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Filter by Email"
              value={filterEmail}
              onChange={(e) => setFilterEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleApplySearch()}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Filter by Customer</InputLabel>
              <Select
                label="Filter by Customer"
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
              >
                {customerOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Filter by Datacentre</InputLabel>
              <Select
                label="Filter by Datacentre"
                value={filterDatacentre}
                onChange={(e) => setFilterDatacentre(e.target.value)}
              >
                {datacentreOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SearchIcon />}
                onClick={handleApplySearch}
              >
                Search
              </Button>
            </Box>
          </Box>

          {loadingContacts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allFilteredSelected}
                        indeterminate={someFilteredSelected}
                        onChange={toggleAllFiltered}
                        disabled={filteredContacts.length === 0}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="overline">Name</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="overline">Email</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="overline">Datacentres</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="overline">Customers</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredContacts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No contacts match the current filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pagedContacts.map((contact) => {
                      const isSelected = selectedContacts.has(contact.email);
                      return (
                        <TableRow
                          key={contact.id ?? contact.email}
                          hover
                          selected={isSelected}
                          onClick={() => toggleContact(contact.email)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              onChange={() => toggleContact(contact.email)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </TableCell>
                          <TableCell>{contact.name}</TableCell>
                          <TableCell>{contact.email}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {(contact.datacentres || []).map((dc) => (
                                <Chip key={dc} label={dc} size="small" />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {(contact.customers || []).map((cu) => (
                                <Chip key={cu} label={cu} size="small" />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {!loadingContacts && filteredContacts.length > 0 && (
            <TablePagination
              component="div"
              count={filteredContacts.length}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
            />
          )}

          {selectedContacts.size > 0 && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.selected',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography variant="body2">
                <strong>{selectedContacts.size}</strong> contact(s) selected
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            disabled={selectedContacts.size === 0 || adding}
            startIcon={adding ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {adding
              ? 'Adding…'
              : `Add${selectedContacts.size > 0 ? ` (${selectedContacts.size})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
